import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { addCart, removeOrder } from '../../redux/menu/cartSlice';
import { FaInfoCircle } from 'react-icons/fa';
import { isProductAvailable, getNextAvailableTime, formatTimeForDisplay } from '../../utils/timeAvailability';
import useTheme from '../../hooks/useTheme';

const FoodItem = ({ image, name, description, price, id, currency, options, is_veg, variants, productOptions, sizes, ProductVariants, categoryName, categoryDescription, subcategoryName, showAddToCart = true, highlightInfoIcon = false, highlight = false, status, onViewDetails, ...rest }) => {
    
    const theme = useTheme();
    const navigate = useNavigate();
    const { id: storeId } = useParams();
    
    // Check if product is available based on time
    const isTimeAvailable = isProductAvailable(rest?.ProductCategory);
    const nextAvailableTime = getNextAvailableTime(rest?.ProductCategory);
    
    // Check if product status is active (status should not be false)
    const isStatusActive = status !== false;
    
    // Product is available only if both time and status are valid
    const isAvailable = isTimeAvailable && isStatusActive;
    
    const dispatch = useDispatch();
    const cartItems = useSelector((state) => state.cart.cart);
    const { profile } = useSelector((state) => state.profile);
    const [variantQuantities, setVariantQuantities] = useState({});

    const [showTooltip, setShowTooltip] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    
    // Get all product images including main image and additional images
    const getAllProductImages = () => {
        const images = [];
        
        // Add main product image
        if (image) {
            images.push(image);
        }
        
        // Add additional product images from rest object
        if (rest?.ProducImages && Array.isArray(rest.ProducImages)) {
            rest.ProducImages.forEach(img => {
                if (img.DocImage?.imageUrl) {
                    images.push(img.DocImage.imageUrl);
                }
            });
        }
        
        // Also check for productImages field (alternative naming)
        if (rest?.productImages && Array.isArray(rest.productImages)) {
            rest.productImages.forEach(img => {
                if (img.DocImage?.imageUrl) {
                    images.push(img.DocImage.imageUrl);
                } else if (typeof img === 'string') {
                    images.push(img);
                }
            });
        }
        
        return images;
    };
    
    const productImages = getAllProductImages();

    // Helper function to format price - remove .00 if decimal part is 00
    const formatPrice = (price) => {
        const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
        const formattedPrice = numericPrice.toFixed(2);
        return formattedPrice.replace('.00', '');
    };
    
    // Memoize variant options to prevent infinite re-renders
    const finalVariantOptions = useMemo(() => {
        // Check for ProductVariants field first (your API field)
        let variantOptions = ProductVariants || [];
        
        // If no ProductVariants, check other possible field names
        if (!variantOptions || variantOptions.length === 0) {
            variantOptions = options || variants || productOptions || sizes || 
                            (rest?.variants) || (rest?.options) || (rest?.productOptions) || (rest?.sizes) || [];
        }
        
        // If variants is a string, try to parse it as JSON
        if (typeof variantOptions === 'string') {
            try {
                variantOptions = JSON.parse(variantOptions);
            } catch (e) {
                variantOptions = [];
            }
        }
        
        // Check for other common field names as fallback
        if (!variantOptions || variantOptions.length === 0) {
            variantOptions = rest?.product_variants || rest?.productVariants || rest?.item_variants || rest?.itemVariants || 
                            rest?.productOptions || rest?.product_options || rest?.variants || rest?.options || [];
        }
        
        return variantOptions || [];
    }, [ProductVariants, options, variants, productOptions, sizes, rest]);

    const finalHasVariants = useMemo(() => {
        return finalVariantOptions && finalVariantOptions.length > 0;
    }, [finalVariantOptions]);
    
    // Sync local state with cart state
    useEffect(() => {
        const syncQuantities = () => {
            const newQuantities = {};
            
            // For products with variants
            if (finalHasVariants) {
                finalVariantOptions.forEach(variant => {
                    const cartItem = cartItems.find(item => item.id === `${id}-${variant.id}`);
                    if (cartItem) {
                        newQuantities[variant.id] = cartItem.quantity;
                    }
                });
            } else {
                // For regular products without variants
                const cartItem = cartItems.find(item => item.id === id);
                if (cartItem) {
                    newQuantities['base'] = cartItem.quantity;
                }
            }
            
            // Only update state if quantities have actually changed
            setVariantQuantities(prevQuantities => {
                const hasChanged = JSON.stringify(prevQuantities) !== JSON.stringify(newQuantities);
                return hasChanged ? newQuantities : prevQuantities;
            });
        };
        
        syncQuantities();
    }, [cartItems, id, finalHasVariants, finalVariantOptions]);
    
    // Get total quantity across all variants
    const getTotalQuantity = () => {
        return Object.values(variantQuantities).reduce((total, qty) => total + qty, 0);
    };
    
    // Get total price across all variants
    const getTotalPrice = () => {
        let total = 0;
        finalVariantOptions.forEach(variant => {
            const qty = variantQuantities[variant.id] || 0;
            const variantPrice = variant.offerPrice && variant.offerPrice !== null ? parseFloat(variant.offerPrice) : parseFloat(variant.price);
            total += variantPrice * qty;
        });
        return total;
    };

    // Helper function to get the effective price (offer price if available, otherwise regular price)
    const getEffectivePrice = (variant) => {
        return variant.offerPrice && variant.offerPrice !== null ? parseFloat(variant.offerPrice) : parseFloat(variant.price);
    };

    // Helper function to check if offer price is available
    const hasOfferPrice = (variant) => {
        return variant.offerPrice && variant.offerPrice !== null && variant.offerPrice !== "";
    };

    const handleViewDetails = () => {
        // Save current state before navigating to product details
        if (onViewDetails) {
            onViewDetails();
        }
        navigate(`/${storeId}/product/${id}`);
    };

    const handleImageModalOpen = () => {
        setCurrentImageIndex(0);
        setShowImageModal(true);
    };

    const handlePreviousImage = () => {
        setCurrentImageIndex((prev) => 
            prev === 0 ? productImages.length - 1 : prev - 1
        );
    };

    const handleNextImage = () => {
        setCurrentImageIndex((prev) => 
            prev === productImages.length - 1 ? 0 : prev + 1
        );
    };

    const handleImageModalClose = () => {
        setShowImageModal(false);
        setCurrentImageIndex(0);
    };

    const handleVariantQuantityChange = (variantId, newQuantity) => {
        if (newQuantity >= 0) {
            const updatedQuantities = { ...variantQuantities };
            
            if (newQuantity > 0) {
                updatedQuantities[variantId] = newQuantity;
                setVariantQuantities(updatedQuantities);
                
                // Add to cart
                if (finalHasVariants) {
                    const variant = finalVariantOptions.find(v => v.id === variantId);
                    const effectivePrice = getEffectivePrice(variant);
                    const cartItem = {
                        id: `${id}-${variantId}`,
                        name: variant.name && variant.name.trim() !== '' ? `${name} (${variant.name})` : name,
                        price: effectivePrice,
                        originalPrice: parseFloat(variant.price),
                        offerPrice: variant.offerPrice,
                        quantity: newQuantity,
                        image,
                        currency,
                        is_veg,
                        variant: variant,
                        storeId: profile?.id // Add store ID
                    };
                    dispatch(addCart(cartItem));
                } else {
                    // Regular product without variants - check if product has offer price
                    const productOfferPrice = rest?.offerPrice;
                    const effectivePrice = productOfferPrice && productOfferPrice !== null ? parseFloat(productOfferPrice) : parseFloat(price);
                    const cartItem = {
                        id: id,
                        name: name,
                        price: effectivePrice,
                        originalPrice: parseFloat(price),
                        offerPrice: productOfferPrice,
                        quantity: newQuantity,
                        image,
                        currency,
                        is_veg,
                        storeId: profile?.id // Add store ID
                    };
                    dispatch(addCart(cartItem));
                }
            } else if (newQuantity === 0) {
                delete updatedQuantities[variantId];
                setVariantQuantities(updatedQuantities);
                
                // Remove from cart
                const cartId = finalHasVariants ? `${id}-${variantId}` : id;
                dispatch(removeOrder(cartId));
            }
        }
    };
    // Regular card layout
    if (!highlight) {
        return (
            <>
            <div className={`bg-white border border-gray-100 transition transform duration-700 p-2.5 md:p-3 lg:p-4 rounded-lg relative min-h-[140px] sm:min-h-[160px] md:min-h-[180px] lg:min-h-[200px] flex flex-col items-center w-full ${
                isAvailable 
                    ? 'hover:shadow-lg md:hover:shadow-xl hover:scale-[1.02] md:hover:scale-105' 
                    : 'opacity-60 cursor-not-allowed'
            }`}>
                {/* Info Tooltip Icon */}
                <div className="absolute top-1.5 md:top-2 left-1.5 md:left-2 z-20">
                    <div className="relative">
                        <button
                            onMouseEnter={() => setShowTooltip(true)}
                            onMouseLeave={() => setShowTooltip(false)}
                            onTouchStart={() => setShowTooltip(!showTooltip)}
                            className="w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center border transition-colors shadow-lg"
                            style={{
                                backgroundColor: highlightInfoIcon ? '#ffffff' : theme.colors.infoIconBg,
                                borderColor: highlightInfoIcon ? '#000000' : 'rgba(255, 255, 255, 0.3)'
                            }}
                        >
                            <FaInfoCircle 
                                className="w-2.5 h-2.5 md:w-3 md:h-3"
                                style={{ color: highlightInfoIcon ? '#000000' : theme.colors.infoIconColor }}
                            />
                        </button>
                        
                        {/* Tooltip Content */}
                        {showTooltip && (
                            <div className="absolute top-6 md:top-8 left-0 z-30 bg-black text-white rounded-lg p-1.5 md:p-2 shadow-lg min-w-[120px] md:min-w-[150px] max-w-[150px] md:max-w-[200px] border border-gray-600">
                                <div className="text-xs font-semibold mb-1 text-blue-300 whitespace-nowrap overflow-hidden text-ellipsis">
                                    {categoryName || 'Category'}
                                </div>
                                {subcategoryName && subcategoryName !== 'All' && (
                                    <div className="text-xs font-medium mb-1 text-green-300 whitespace-nowrap overflow-hidden text-ellipsis">
                                        {subcategoryName}
                                    </div>
                                )}
                                {categoryDescription && (
                                    <div className="text-xs text-gray-200 leading-relaxed">
                                        {categoryDescription}
                                    </div>
                                )}
                                {/* Tooltip Arrow */}
                                <div className="absolute -top-2 left-2 md:left-3 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-black"></div>
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Badge Ribbon */}
                {rest?.badge && (
                    <div className="absolute top-0 right-0 z-30 w-28 h-20 overflow-hidden">
                        <div className="absolute -top-2 -right-2 text-white text-[4px] font-bold px-1 py-1 shadow-lg transform rotate-45" style={{
                            top : "20%",
                            left : "25%",
                            width : "100%",
                            fontSize : "10px",
                            textAlign : "center",
                            backgroundColor: is_veg ? theme.colors.badgeVeg : theme.colors.badgeNonVeg
                        }}>
                            {rest.badge}
                        </div>
                    </div>
                )}
                
                {/* Availability Message */}
                {!isAvailable && (
                    <div className="absolute top-0 left-0 right-0 bg-red-500 text-white text-center py-1 px-2 text-xs font-bold z-30">
                        {'Currently Unavailable'}
                        {isStatusActive && nextAvailableTime && (
                            <div className="text-xs opacity-90">
                                Available at {formatTimeForDisplay(nextAvailableTime)}
                            </div>
                        )}
                    </div>
                )}
                
                {/* Product Image */}
                <div className="relative flex items-center justify-center">
                    <img 
                        className="w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20 lg:w-24 lg:h-24 xl:w-32 xl:h-32 mx-auto transform transition duration-300 hover:scale-105 object-cover cursor-pointer" 
                        src={`${image}`} 
                        alt={name}
                        onClick={handleImageModalOpen}
                    />
                    
                    {/* Seasonal Badge */}
                    {rest?.seasonal && (
                        <div style={{
                            position: 'absolute',
                            bottom: '0px',
                            right: '0px',
                            background: `linear-gradient(to right, ${theme.colors.seasonalBadgeFrom}, ${theme.colors.seasonalBadgeTo})`,
                            color: 'white',
                            fontSize: '7px',
                            fontWeight: 'bold',
                            padding: '2px 4px',
                            borderRadius: '12px 0px 0px 0px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            zIndex: 10
                        }}>
                            Special Edition
                        </div>
                    )}
                </div>
                
                {/* Product Name - Separate from other content */}
                <div className="flex justify-center mt-0 mb-1 md:mb-1.5 w-full">
                    <h1 className="text-gray-900 poppins text-xs sm:text-sm md:text-base lg:text-lg text-center font-medium px-1 md:px-2 leading-tight">{name}</h1>
                </div>
                
                {/* Content Section */}
                <div className="flex flex-col items-center space-y-1 md:space-y-2 w-full">
                    {/* Variant Selection with Individual Quantity Controls */}
                    {finalHasVariants && (
                        <div className="flex flex-col gap-2 md:gap-3 justify-center mb-1 md:mb-2 w-full">
                            {finalVariantOptions.map((variant) => {
                                const variantQty = variantQuantities[variant.id] || 0;
                                return (
                                    <div key={variant.id} className="flex items-center justify-between bg-gray-50 rounded-md md:rounded-lg p-1.5 md:p-2">
                                        <div className="flex-1">
                                            <div className="text-xs font-medium text-gray-700 truncate">{variant.name}</div>
                                            <div className="text-xs font-bold" style={{ color: theme.colors.variantPriceText }}>
                                                {hasOfferPrice(variant) ? (
                                                    <div className="flex flex-col">
                                                        <span className="font-bold" style={{ color: theme.colors.offerPriceText }}>{currency} {formatPrice(getEffectivePrice(variant))}</span>
                                                        <span className="text-gray-500 line-through text-xs">{currency} {formatPrice(variant.price)}</span>
                                                    </div>
                                                ) : (
                                                    <span>{currency} {formatPrice(variant.price)}</span>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {showAddToCart && (
                                            variantQty === 0 ? (
                                                <button 
                                                    onClick={handleViewDetails}
                                                    disabled={!isAvailable}
                                                    className={`px-2 py-1 md:px-4 md:py-2 text-xs md:text-sm font-semibold rounded-full transition-colors shadow-sm md:shadow-md ${
                                                        isAvailable 
                                                            ? '' 
                                                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                    }`}
                                                    style={isAvailable ? {
                                                        backgroundColor: theme.colors.productAddButtonBg,
                                                        color: theme.colors.productAddButtonText,
                                                        border: `1px solid ${theme.colors.productAddButtonBorder}`
                                                    } : {}}
                                                >
                                                    {isAvailable ? 'ADD' : (!isStatusActive ? 'UNAVAILABLE' : 'NOT AVAILABLE')}
                                                </button>
                                            ) : (
                                                <div className="flex items-center space-x-1.5 md:space-x-2">
                                                    <button 
                                                        onClick={() => handleVariantQuantityChange(variant.id, variantQty - 1)}
                                                        disabled={!isAvailable}
                                                        className={`w-5 h-5 md:w-6 md:h-6 bg-white text-gray-700 rounded-full flex items-center justify-center transition-all duration-200 text-xs md:text-sm font-bold shadow-sm border border-gray-200 ${
                                                            isAvailable ? 'hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'
                                                        }`}
                                                    >
                                                        -
                                                    </button>
                                                    <span className="text-xs md:text-sm font-bold text-gray-800 min-w-[15px] md:min-w-[20px] text-center">{variantQty}</span>
                                                    <button 
                                                        onClick={() => handleVariantQuantityChange(variant.id, variantQty + 1)}
                                                        disabled={!isAvailable}
                                                        className={`w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center transition-all duration-200 text-xs md:text-sm font-bold shadow-sm ${
                                                            isAvailable ? '' : 'opacity-50 cursor-not-allowed'
                                                        }`}
                                                        style={isAvailable ? {
                                                            backgroundColor: theme.colors.productAddButtonBg,
                                                            color: theme.colors.productAddButtonText,
                                                            border: `1px solid ${theme.colors.productAddButtonBorder}`
                                                        } : {}}
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                            )
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    
                    {/* Total Price Display - Only for products with variants */}
                    {finalHasVariants && getTotalQuantity() > 0 && (
                        <div className="text-center mt-1 md:mt-2">
                            <div className="text-xs text-gray-600">Total: {getTotalQuantity()} items</div>
                            <div className={`text-gray-900 poppins text-sm md:text-base font-bold ${currency === 'AED' ? 'currency-aed' : ''}`}>
                                {currency} {formatPrice(getTotalPrice())}
                            </div>
                        </div>
                    )}
                    
                    {/* Regular Product (No Variants) */}
                    {!finalHasVariants && (
                        <div className="flex flex-col items-center">
                            <div className={`text-gray-900 poppins text-xs sm:text-sm md:text-base font-bold ${currency === 'AED' ? 'currency-aed' : ''} text-center mb-1 md:mb-1.5`}>
                                {rest?.offerPrice && rest?.offerPrice !== null ? (
                                    <div className="flex flex-col items-center">
                                        <span className="font-bold" style={{ color: theme.colors.offerPriceText }}>{currency} {formatPrice(parseFloat(rest.offerPrice) * (variantQuantities['base'] || 1))}</span>
                                        <span className="text-gray-500 line-through text-xs">{currency} {formatPrice(parseFloat(price) * (variantQuantities['base'] || 1))}</span>
                                    </div>
                                ) : (
                                    <span>{currency} {formatPrice(parseFloat(price) * (variantQuantities['base'] || 1))}</span>
                                )}
                            </div>
                            
                            {showAddToCart && (
                                variantQuantities['base'] === 0 || !variantQuantities['base'] ? (
                                    <button 
                                        onClick={handleViewDetails}
                                        disabled={!isAvailable}
                                        className={`px-3 py-1.5 sm:px-4 sm:py-2 md:px-6 md:py-3 focus:outline-none poppins rounded-full transform transition duration-300 text-xs sm:text-sm md:text-base font-medium shadow-sm md:shadow-md ${
                                            isAvailable 
                                                ? 'hover:scale-105' 
                                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        }`}
                                        style={isAvailable ? {
                                            backgroundColor: theme.colors.productAddButtonBg,
                                            color: theme.colors.productAddButtonText,
                                            border: `1px solid ${theme.colors.productAddButtonBorder}`
                                        } : {}}
                                    >
                                        {isAvailable ? 'Add to Cart' : (!isStatusActive ? 'Unavailable' : 'Not Available')}
                                    </button>
                                ) : (
                                    <div className="flex items-center space-x-2 md:space-x-3 bg-gray-50 rounded-full px-2 py-1.5 md:px-3 md:py-2 shadow-sm">
                                        <button 
                                            onClick={() => handleVariantQuantityChange('base', variantQuantities['base'] - 1)}
                                            disabled={!isAvailable}
                                            className={`w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 bg-white text-gray-700 rounded-full flex items-center justify-center transition-all duration-200 text-sm md:text-lg font-bold shadow-sm border border-gray-200 ${
                                                isAvailable ? 'hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'
                                            }`}
                                        >
                                            -
                                        </button>
                                        <span className="text-sm sm:text-base md:text-lg font-bold text-gray-800 min-w-[20px] sm:min-w-[25px] md:min-w-[30px] text-center">{variantQuantities['base']}</span>
                                        <button 
                                            onClick={() => handleVariantQuantityChange('base', variantQuantities['base'] + 1)}
                                            disabled={!isAvailable}
                                            className={`w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center transition-all duration-200 text-sm md:text-lg font-bold shadow-sm ${
                                                isAvailable ? '' : 'opacity-50 cursor-not-allowed'
                                            }`}
                                            style={isAvailable ? {
                                                backgroundColor: theme.colors.productAddButtonBg,
                                                color: theme.colors.productAddButtonText,
                                                border: `1px solid ${theme.colors.productAddButtonBorder}`
                                            } : {}}
                                        >
                                            +
                                        </button>
                                    </div>
                                )
                            )}
                        </div>
                    )}
                </div>
            </div>
            
            {/* Image Modal */}
            {showImageModal && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-0 sm:p-1" onClick={handleImageModalClose}>
                    <div className="relative w-full bg-white rounded-lg overflow-hidden shadow-2xl" style={{ height: '80vh', maxWidth: '95vw', marginTop: '8vh', marginBottom: '12vh' }} onClick={(e) => e.stopPropagation()}>
                        <button 
                            onClick={handleImageModalClose}
                            className="absolute top-4 right-4 z-10 bg-red-500 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-red-600 transition-all duration-200 shadow-lg border-2 border-white"
                            style={{ fontSize: '20px', fontWeight: 'bold' }}
                        >
                            ×
                        </button>
                        
                        {/* Navigation buttons - only show if multiple images */}
                        {productImages.length > 1 && (
                            <>
                                <button 
                                    onClick={handlePreviousImage}
                                    className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black bg-opacity-50 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-75 transition-all duration-200"
                                >
                                    ‹
                                </button>
                                <button 
                                    onClick={handleNextImage}
                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black bg-opacity-50 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-75 transition-all duration-200"
                                >
                                    ›
                                </button>
                            </>
                        )}
                        
                        <img 
                            src={productImages[currentImageIndex]} 
                            alt={`${name} ${currentImageIndex + 1} of ${productImages.length}`}
                            className="w-full h-full object-contain"
                        />
                        
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                            <h3 className="text-white text-lg font-semibold">{name}</h3>
                        </div>
                        
                        {/* Image dots indicator - only show if multiple images */}
                        {productImages.length > 1 && (
                            <div className="absolute bottom-16 left-0 right-0 flex justify-center space-x-2 p-4">
                                {productImages.map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setCurrentImageIndex(index)}
                                        className={`w-3 h-3 rounded-full transition-all duration-200 ${
                                            index === currentImageIndex 
                                                ? 'bg-gray-800 scale-125' 
                                                : 'bg-gray-400 hover:bg-gray-600'
                                        }`}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
            </>
        )
    }

    // Highlighted card layout (spans 2 columns)
    return (
        <>
        <div className={`col-span-2 bg-gradient-to-br from-primary/10 via-primary/5 to-primary/10 border-2 border-primary/30 rounded-xl md:rounded-2xl p-3 md:p-4 lg:p-6 relative overflow-hidden shadow-xl md:shadow-2xl transition-all duration-500 ${
            isAvailable 
                ? 'hover:shadow-2xl md:hover:shadow-3xl hover:scale-[1.01] md:hover:scale-[1.02]' 
                : 'opacity-60 cursor-not-allowed'
        }`}>
            {/* Highlight Badge */}
            <div className="absolute top-2 md:top-3 left-2 md:left-3 z-20">
                <div className="px-2 md:px-3 py-1 rounded-full text-xs font-bold shadow-lg animate-pulse border" 
                    style={{
                        background: `linear-gradient(to right, ${theme.colors.featuredBadgeFrom}, ${theme.colors.featuredBadgeTo})`,
                        color: theme.colors.featuredBadgeText,
                        borderColor: theme.colors.featuredBadgeTo
                    }}>
                    ⭐ FEATURED
                </div>
            </div>

            {/* Info Tooltip Icon */}
            <div className="absolute bottom-2 md:bottom-3 right-2 md:right-3 z-20">
                <div className="relative">
                    <button
                        onMouseEnter={() => setShowTooltip(true)}
                        onMouseLeave={() => setShowTooltip(false)}
                        onTouchStart={() => setShowTooltip(!showTooltip)}
                        className="w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center border transition-colors shadow-lg"
                        style={{
                            backgroundColor: highlightInfoIcon ? '#ffffff' : theme.colors.infoIconBg,
                            borderColor: highlightInfoIcon ? '#000000' : 'rgba(255, 255, 255, 0.3)'
                        }}
                    >
                        <FaInfoCircle 
                            className="w-3 h-3 md:w-4 md:h-4"
                            style={{ color: highlightInfoIcon ? '#000000' : theme.colors.infoIconColor }}
                        />
                    </button>
                    
                    {/* Tooltip Content */}
                    {showTooltip && (
                        <div className="absolute bottom-8 md:bottom-10 right-0 z-30 bg-black text-white rounded-lg p-2 md:p-3 shadow-lg min-w-[150px] md:min-w-[200px] max-w-[200px] md:max-w-[250px] border border-gray-600">
                            <div className="text-xs md:text-sm font-semibold mb-1 md:mb-2 text-blue-300 whitespace-nowrap overflow-hidden text-ellipsis">
                                {categoryName || 'Category'}
                            </div>
                            {subcategoryName && subcategoryName !== 'All' && (
                                <div className="text-xs md:text-sm font-medium mb-1 md:mb-2 text-green-300 whitespace-nowrap overflow-hidden text-ellipsis">
                                    {subcategoryName}
                                </div>
                            )}
                            {categoryDescription && (
                                <div className="text-xs md:text-sm text-gray-200 leading-relaxed">
                                    {categoryDescription}
                                </div>
                            )}
                            {/* Tooltip Arrow */}
                            <div className="absolute -bottom-2 right-3 md:right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black"></div>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Badge Ribbon */}
            {rest?.badge && (
                <div className="absolute top-0 right-0 z-30 w-28 h-20 overflow-hidden ">
                    <div 
                    style={{
                        fontSize : "12px",
                        top:"20%",
                        left:"25%",
                        textAlign : "center",
                        width : "100%",
                        backgroundColor: is_veg ? theme.colors.badgeVeg : theme.colors.badgeNonVeg
                        
                    }}
                    className="absolute -top-2 -right-2 text-white text-[4px] font-bold px-1 py-1 shadow-lg transform rotate-45">
                        {rest.badge}
                    </div>
            </div>
            )}
            
            {/* Availability Message for Highlighted Card */}
            {!isAvailable && (
                <div className="absolute top-0 left-0 right-0 bg-red-500 text-white text-center py-2 px-3 text-sm font-bold z-30 rounded-t-xl md:rounded-t-2xl">
                    {!isStatusActive ? 'Currently Unavailable' : 'Not Available Now'}
                    {isStatusActive && nextAvailableTime && (
                        <div className="text-xs opacity-90 mt-1">
                            Available at {formatTimeForDisplay(nextAvailableTime)}
                        </div>
                    )}
                </div>
            )}
            
            <div className="flex flex-col md:flex-row items-center md:items-start gap-3 md:gap-4 lg:gap-6">
                {/* Product Image */}
                <div className="flex-shrink-0 relative">
                    <img 
                        className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 lg:w-40 lg:h-40 xl:w-48 xl:h-48 object-cover rounded-xl md:rounded-2xl shadow-lg md:shadow-xl transform transition duration-300 hover:scale-105 cursor-pointer" 
                        src={`${image}`}
                        alt={name}
                        onClick={handleImageModalOpen}
                    />
                    
                    {/* Seasonal Badge */}
                    {rest?.seasonal && (
                        <div style={{
                            position: 'absolute',
                            bottom: '0px',
                            right: '0px',
                            background: 'linear-gradient(to right, #f97316, #dc2626)',
                            color: 'white',
                            fontSize: '7px',
                            fontWeight: 'bold',
                            padding: '3px 6px',
                            borderRadius: '12px 0px 0px 0px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            zIndex: 10
                        }}>
                            SEASONAL
                        </div>
                    )}
                </div>

                {/* Product Details */}
                <div className="flex-1 text-center md:text-left">
                    <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 poppins mb-1 md:mb-2 lg:mb-3">
                        {name}
                    </h1>
                    
                    {description && (
                        <p className="text-gray-600 text-xs sm:text-sm md:text-base mb-2 md:mb-3 lg:mb-4 leading-relaxed">
                            {description}
                        </p>
                    )}

                    {/* Variant Selection with Individual Quantity Controls */}
                    {finalHasVariants && (
                        <div className="space-y-2 md:space-y-3 mb-2 md:mb-3 lg:mb-4">
                            {finalVariantOptions.map((variant) => {
                                const variantQty = variantQuantities[variant.id] || 0;
                                return (
                                    <div key={variant.id} className="flex items-center justify-between bg-white/80 backdrop-blur-sm rounded-lg md:rounded-xl p-2 md:p-3 shadow-sm md:shadow-md">
                                        <div className="flex-1">
                                            <div className="text-xs sm:text-sm md:text-base font-semibold text-gray-800">{variant.name}</div>
                                            <div className="text-xs sm:text-sm md:text-base font-bold" style={{ color: theme.colors.variantPriceText }}>
                                                {hasOfferPrice(variant) ? (
                                                    <div className="flex flex-col">
                                                        <span className="font-bold" style={{ color: theme.colors.offerPriceText }}>{currency} {formatPrice(getEffectivePrice(variant))}</span>
                                                        <span className="text-gray-500 line-through text-xs">{currency} {formatPrice(variant.price)}</span>
                                                    </div>
                                                ) : (
                                                    <span>{currency} {formatPrice(variant.price)}</span>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {showAddToCart && (
                                            variantQty === 0 ? (
                                                <button 
                                                    onClick={handleViewDetails}
                                                    disabled={!isAvailable}
                                                    className={`px-3 py-1.5 md:px-6 md:py-2 lg:px-8 lg:py-3 text-xs sm:text-sm md:text-base font-semibold rounded-full transition-colors shadow-sm md:shadow-md ${
                                                        isAvailable 
                                                            ? '' 
                                                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                    }`}
                                                    style={isAvailable ? {
                                                        backgroundColor: theme.colors.productAddButtonBg,
                                                        color: theme.colors.productAddButtonText,
                                                        border: `1px solid ${theme.colors.productAddButtonBorder}`
                                                    } : {}}
                                                >
                                                    {isAvailable ? 'ADD' : (!isStatusActive ? 'UNAVAILABLE' : 'NOT AVAILABLE')}
                                                </button>
                                            ) : (
                                                <div className="flex items-center space-x-2 md:space-x-3">
                                                    <button 
                                                        onClick={() => handleVariantQuantityChange(variant.id, variantQty - 1)}
                                                        disabled={!isAvailable}
                                                        className={`w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-10 lg:h-10 bg-white text-gray-700 rounded-full flex items-center justify-center transition-all duration-200 text-sm md:text-lg font-bold shadow-sm border border-gray-200 ${
                                                            isAvailable ? 'hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'
                                                        }`}
                                                    >
                                                        -
                                                    </button>
                                                    <span className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gray-800 min-w-[20px] sm:min-w-[25px] md:min-w-[30px] text-center">{variantQty}</span>
                                                    <button 
                                                        onClick={() => handleVariantQuantityChange(variant.id, variantQty + 1)}
                                                        disabled={!isAvailable}
                                                        className={`w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-10 lg:h-10 rounded-full flex items-center justify-center transition-all duration-200 text-sm md:text-lg font-bold shadow-sm ${
                                                            isAvailable ? '' : 'opacity-50 cursor-not-allowed'
                                                        }`}
                                                        style={isAvailable ? {
                                                            backgroundColor: theme.colors.productAddButtonBg,
                                                            color: theme.colors.productAddButtonText,
                                                            border: `1px solid ${theme.colors.productAddButtonBorder}`
                                                        } : {}}
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                            )
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    
                    {/* Total Price Display - Only for products with variants */}
                    {finalHasVariants && getTotalQuantity() > 0 && (
                        <div className="text-center md:text-left mb-2 md:mb-3 lg:mb-4">
                            <div className="text-xs sm:text-sm md:text-base text-gray-600 mb-1">Total: {getTotalQuantity()} items</div>
                            <div className={`text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-primary poppins ${currency === 'AED' ? 'currency-aed' : ''}`}>
                                {currency} {formatPrice(getTotalPrice())}
                            </div>
                        </div>
                    )}
                    
                    {/* Regular Product (No Variants) */}
                    {!finalHasVariants && (
                        <div className="text-center md:text-left">
                            <div className={`text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-primary poppins mb-2 md:mb-3 lg:mb-4 ${currency === 'AED' ? 'currency-aed' : ''}`}>
                                {rest?.offerPrice && rest?.offerPrice !== null ? (
                                    <div className="flex flex-col items-center md:items-start">
                                        <span className="font-bold" style={{ color: theme.colors.offerPriceText }}>{currency} {formatPrice(parseFloat(rest.offerPrice) * (variantQuantities['base'] || 1))}</span>
                                        <span className="text-gray-500 line-through text-sm md:text-base lg:text-lg">{currency} {formatPrice(parseFloat(price) * (variantQuantities['base'] || 1))}</span>
                                    </div>
                                ) : (
                                    <span>{currency} {formatPrice(parseFloat(price) * (variantQuantities['base'] || 1))}</span>
                                )}
                            </div>
                            
                            {showAddToCart && (
                                variantQuantities['base'] === 0 || !variantQuantities['base'] ? (
                                    <button 
                                        onClick={handleViewDetails}
                                        disabled={!isAvailable}
                                        className={`px-4 py-2 sm:px-6 sm:py-2.5 md:px-8 md:py-3 lg:px-12 lg:py-4 text-sm sm:text-base md:text-lg lg:text-xl font-semibold rounded-full transition-colors shadow-sm md:shadow-md ${
                                            isAvailable 
                                                ? '' 
                                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        }`}
                                        style={isAvailable ? {
                                            backgroundColor: theme.colors.productAddButtonBg,
                                            color: theme.colors.productAddButtonText,
                                            border: `1px solid ${theme.colors.productAddButtonBorder}`
                                        } : {}}
                                    >
                                        {isAvailable ? 'Add to Cart' : (!isStatusActive ? 'Unavailable' : 'Not Available')}
                                    </button>
                                ) : (
                                    <div className="flex items-center justify-center md:justify-start space-x-3 md:space-x-4 bg-white/80 backdrop-blur-sm rounded-xl md:rounded-2xl px-4 py-3 md:px-6 md:py-4 shadow-md md:shadow-lg">
                                        <button 
                                            onClick={() => handleVariantQuantityChange('base', variantQuantities['base'] - 1)}
                                            disabled={!isAvailable}
                                            className={`w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 lg:w-12 lg:h-12 bg-white text-gray-700 rounded-full flex items-center justify-center transition-all duration-200 text-lg md:text-xl font-bold shadow-sm border border-gray-200 ${
                                                isAvailable ? 'hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'
                                            }`}
                                        >
                                            -
                                        </button>
                                        <span className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-800 min-w-[30px] sm:min-w-[35px] md:min-w-[40px] text-center">{variantQuantities['base']}</span>
                                        <button 
                                            onClick={() => handleVariantQuantityChange('base', variantQuantities['base'] + 1)}
                                            disabled={!isAvailable}
                                            className={`w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center transition-all duration-200 text-lg md:text-xl font-bold shadow-sm ${
                                                isAvailable ? '' : 'opacity-50 cursor-not-allowed'
                                            }`}
                                            style={isAvailable ? {
                                                backgroundColor: theme.colors.productAddButtonBg,
                                                color: theme.colors.productAddButtonText,
                                                border: `1px solid ${theme.colors.productAddButtonBorder}`
                                            } : {}}
                                        >
                                            +
                                        </button>
                                    </div>
                                )
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
        
        {/* Image Modal */}
        {showImageModal && (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-0 sm:p-1" onClick={handleImageModalClose}>
                <div className="relative w-full bg-white rounded-lg overflow-hidden shadow-2xl" style={{ height: '80vh', maxWidth: '95vw', marginTop: '8vh', marginBottom: '12vh' }} onClick={(e) => e.stopPropagation()}>
                    <button 
                        onClick={handleImageModalClose}
                        className="absolute top-4 right-4 z-10 bg-red-500 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-red-600 transition-all duration-200 shadow-lg border-2 border-white"
                        style={{ fontSize: '20px', fontWeight: 'bold' }}
                    >
                        ×
                    </button>
                    
                    {/* Navigation buttons - only show if multiple images */}
                    {productImages.length > 1 && (
                        <>
                            <button 
                                onClick={handlePreviousImage}
                                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black bg-opacity-50 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-75 transition-all duration-200"
                            >
                                ‹
                            </button>
                            <button 
                                onClick={handleNextImage}
                                className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black bg-opacity-50 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-75 transition-all duration-200"
                            >
                                ›
                            </button>
                        </>
                    )}
                    
                    <img 
                        src={productImages[currentImageIndex]} 
                        alt={`${name} ${currentImageIndex + 1} of ${productImages.length}`}
                        className="w-full h-full object-contain"
                    />
                    
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                        <h3 className="text-white text-lg font-semibold">{name}</h3>
                    </div>
                    
                    {/* Image dots indicator - only show if multiple images */}
                    {productImages.length > 1 && (
                        <div className="absolute bottom-16 left-0 right-0 flex justify-center space-x-2 p-4">
                            {productImages.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentImageIndex(index)}
                                    className={`w-3 h-3 rounded-full transition-all duration-200 ${
                                        index === currentImageIndex 
                                            ? 'bg-gray-800 scale-125' 
                                            : 'bg-gray-400 hover:bg-gray-600'
                                    }`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        )}
        </>
    )
}

export default FoodItem
